/**
 * Finding an iTunes track in the Fadila library.
 *
 * The problem: an iTunes playlist names its tracks by a `Track ID` that means
 * nothing outside that one library file, and their files by an absolute path
 * on a machine that no longer exists. Fadila names tracks by Google Drive /
 * OneDrive / Dropbox id. There is no shared identifier at all, so the only
 * bridge is the metadata both sides read out of the same audio files.
 *
 * That turns out to be a strong bridge, because Fadila fills `artist`, `album`
 * and `title` from a `jsmediatags` read of the file and iTunes filled its own
 * fields from the same tags. Measured against the reference library (41,038
 * iTunes tracks, 39,082 Drive tracks in Fadila):
 *
 * | Set                                  | Matched |
 * |--------------------------------------|---------|
 * | "**** All Star Special ****" (2,621) |  99.8%  |
 * | "*** Honorable Mention ***" (482)    | 100%    |
 * | Everything under the music folder    |  99.2%  |
 * | "Essential Mix" (1,046)              |  98.8%  |
 *
 * What this deliberately does *not* do is fuzzy matching. Every rule here is
 * exact equality on normalized text; length is used only to decide *which* of
 * several identically-tagged tracks is meant, never whether two tracks are the
 * same. A wrong track silently added to a curated playlist is worse than a
 * track reported missing, and with these numbers there is no reason to reach
 * for anything looser. See docs/ITUNES-IMPORT.md section 3.
 */

import MusicLibrary from '../../services/library/musicLibrary';
import Source from '../../models/source';
import Track from '../../models/track';
import { normalizeForSearch } from '../trackSearch';
import { fileNameFromLocation, ItunesTrack } from './itunesPlist';

/**
 * Punctuation to fold away before comparing, on top of the case and accent
 * folding `normalizeForSearch` does.
 *
 * Why it is needed: iTunes and the file system disagree about punctuation all
 * the time. `Re:Sound` is `Re_Sound` in a file name because Windows forbids a
 * colon; `Bustin' Up` acquires a typographic apostrophe; `Point of No Return`
 * and `Point Of No Return` differ only in case, which is already handled, but
 * `Hell For Heroes Mix` vs `Hell_For_Heroes_Mix` is not.
 *
 * Why it is an explicit character set rather than the neater
 * `/[^\p{L}\p{N}]+/gu`: a Unicode property escape would drop a title written
 * entirely in a non-Latin script to the empty string, since
 * `normalizeForSearch` only folds Latin and Nordic letters to ASCII. Measured
 * on the reference library the two agree to within two tracks out of 37,138,
 * so the safe one wins.
 */
const punctuation = /[\s\-_.,;:!?'"`´‘’“”()[\]{}<>|/\\&+*#~@$%^=°·•–—]+/g;

/**
 * C0 and C1 control characters, which turn up in tags read with the wrong
 * charset: a Windows-1252 curly quote (byte 0x91) decoded as Latin-1 becomes
 * U+0091, invisible and unequal to anything. The reference library holds
 * "Move U+0091n' Rhythm" against iTunes' "Move ‘n' Rhythm", and eighteen
 * records like it. Tabs are in here too; ID3 padding leaves those.
 */
// eslint-disable-next-line no-control-regex
const controlCharacters = /[\u0000-\u001f\u007f-\u009f]+/g;

/**
 * Case, accents, control characters and punctuation folded.
 *
 * A title made of nothing but punctuation - Quarashi's "/=", a "??", a "#",
 * four of each in the reference library and its iTunes export - would fold
 * to nothing and never match anything, including itself. For those the
 * punctuation is the title, so it is compared as it is.
 */
export function normalizeForMatch(value: string | undefined) {
  if (!value) {
    return '';
  }

  const folded = normalizeForSearch(value)
    .replace(controlCharacters, ' ')
    .replace(punctuation, ' ')
    .trim();

  if (folded) {
    return folded;
  }

  return normalizeForSearch(value).replace(controlCharacters, '').trim();
}

/*
  Breaking a tie on length.

  All four thresholds below apply only to candidates that are *already* equal
  on the rule that matched - same artist, same album, same title. The question
  they answer is never "is this the same track" but "which of these
  same-named tracks is it", and the population is almost entirely vinyl rips
  where every cut on one record is tagged "Untitled".
*/

/**
 * How close two durations have to be to count as the same recording, in
 * milliseconds.
 *
 * Two seconds because the same file read by two decoders disagrees by a frame
 * or two, and an iTunes duration is an integer while Fadila's can be
 * fractional - but two different cuts of a record are further apart.
 */
export const durationTolerance = 2000;

/**
 * How much nearer the nearest candidate has to be than the runner-up for the
 * length to have actually said something.
 *
 * Without this a tie inside the tolerance was given up on, which threw away
 * information already in hand. The real case, from the reference library:
 * Drumcode DC23 has two "Safety Session - Untitled" cuts, and Fadila holds
 * both at 306.687 s and 307.784 s. iTunes' 01 is 306.651 s and its 03 is
 * 307.748 s — each is 0.04 s from one candidate and over a second from the
 * other, so each has an obvious and *different* answer.
 *
 * One second because that is the point below which the two candidates are
 * equally plausible: Zync ZYNC004's two "Untitled" cuts sit 1.2 s and 1.5 s
 * from the iTunes duration, and nothing about that pair is decidable.
 */
export const clearMargin = 1000;

/**
 * When *nothing* is within `durationTolerance`: how far off the nearest may
 * be, and how many times farther every other candidate has to be, for the
 * nearest to be worth **putting in front of the user** - which is all this
 * does. It is not a match.
 *
 * The case it describes: Chris Liebing Audio CLAU05 has two "Sven Dedek -
 * Untitled" records in Fadila at 394.4 s and 461.2 s, and iTunes says
 * 391.9 s. The first is 2.5 s out, which is a different trim of the same cut
 * or a different cut of similar length; the second is 69 s out, which is
 * certainly other music. Something is probably right there, and nothing is
 * sure, so it is reported as `uncertain` with both numbers and left out of
 * the import unless the user asks for it.
 *
 * Ten times and ten seconds are the bounds of "worth showing as a near
 * miss". Outside them the answer is a plain ambiguity, because there is not
 * even a candidate worth naming.
 */
export const looseTolerance = 10000;
export const looseRatio = 10;

/**
 * How close to the iTunes length a candidate has to be to count as an exact
 * hit, and how far behind the runner-up has to be for the hit to decide.
 *
 * Two decoders reading one file disagree by a frame or two - single-digit
 * milliseconds, tens at the outside - so a candidate within `exactHit` *is*
 * that file as far as a length can say. What it cannot rule out on its own
 * is a second candidate also within a few milliseconds; hence the runner-up
 * has to be `exactHitMargin` away and `exactHitRatio` times farther.
 *
 * The case: Akala's "Bullshit (Chase and Status Remix)" and its instrumental
 * share a title, because ID3v1 cuts titles at thirty characters. They are
 * 605 ms apart, and each iTunes entry is within 4 ms of its own file. The
 * old margin of a clear second called that a tie; 4 ms against 605 is not
 * one. Measured on the reference library this decides 44 rows, every one of
 * them a hit under 10 ms with the runner-up 400 ms or more out.
 */
export const exactHit = 50;
export const exactHitMargin = 250;
export const exactHitRatio = 10;

/**
 * How far apart a set of candidates may be, end to end, and still be the
 * same recording several times over.
 *
 * The library holds many records twice - the same release uploaded under two
 * date folders, a rip and its rerip, an mp3 and an m4a of one album - with
 * lengths that agree to the millisecond or nearly. Fifty milliseconds is
 * decoder jitter; two different cuts off one record are not that close.
 * Measured on the reference library 587 of 728 ambiguities are this, and
 * for those any candidate is the right music. Which copy is the user's
 * choice, so it sits behind a checkbox - see ImportPlan.includeCopies.
 */
export const copyTolerance = 50;

/** Which rule found a match. Reported so a run can be sanity-checked. */
export enum MatchMethod {
  /** Artist, album and title all equal. The one that does the work. */
  ArtistAlbumTitle = 'artist+album+title',
  /** Artist and title equal - the album was renamed or is missing. */
  ArtistTitle = 'artist+title',
  /** The iTunes *file name* equals a Fadila title. Untagged files. */
  FileName = 'filename',
  /** Title alone. Last resort, and only when it is unique in the library. */
  Title = 'title',
}

export type MatchResult =
  | { kind: 'matched'; track: Track; method: MatchMethod; byDuration: boolean }
  /**
   * One candidate stands out, but not by enough to call it a match: nothing
   * is inside the duration tolerance and this is merely the nearest by a
   * long way (`looseTolerance` / `looseRatio`).
   *
   * Reported rather than imported. Both distances travel with it so the user
   * is shown *why* it is doubtful - "2.5 s out, next 69 s away" is a
   * judgement someone can actually make - and it is added only if they ask
   * for it.
   */
  | {
      kind: 'uncertain';
      track: Track;
      candidates: Track[];
      method: MatchMethod;
      /** How far the offered track is from the iTunes length, in ms. */
      distance: number;
      /** How far the next-nearest candidate is, in ms. */
      runnerUpDistance: number;
    }
  /**
   * Several candidates the length cannot separate.
   *
   * `sameRecording` is true when it cannot separate them because there is
   * nothing to separate: every candidate is within `copyTolerance` of every
   * other and the nearest is inside the tolerance, so the library holds one
   * recording several times and any of them is the right music. Those, and
   * only those, can be imported - by the user's choice, one copy each. The
   * rest are never imported.
   */
  | { kind: 'ambiguous'; candidates: Track[]; method: MatchMethod; sameRecording: boolean }
  | { kind: 'unmatched' };

/**
 * The library turned inside out: three lookup tables from normalized metadata
 * to the tracks holding it.
 *
 * Built once per import and reused for every playlist, because building it
 * means normalizing 39,000 * 3 strings - about a second - and matching against
 * it is then a handful of map lookups per track.
 */
export interface MatchIndex {
  byArtistAlbumTitle: Map<string, Track[]>;
  byArtistTitle: Map<string, Track[]>;
  byTitle: Map<string, Track[]>;
  /** How many library tracks went in, for the report. */
  trackCount: number;
}

function push(map: Map<string, Track[]>, key: string, track: Track) {
  const existing = map.get(key);

  if (existing) {
    existing.push(track);
  } else {
    map.set(key, [track]);
  }
}

/**
 * Which library buckets are worth indexing.
 *
 * Only tracks that come from a file have tags to match on. A YouTube video's
 * title is the uploader's headline ("Artist - Title (Official Video)"), a
 * Spotify or SoundCloud track is not the user's file at all, and a podcast
 * episode is matched by feed elsewhere. Indexing them would only invite a
 * wrong match on the title-alone rule.
 */
const matchableSources = [Source.File, Source.GoogleDrive, Source.OneDrive, Source.Dropbox];

export function buildMatchIndex(library: MusicLibrary): MatchIndex {
  const byArtistAlbumTitle = new Map<string, Track[]>();
  const byArtistTitle = new Map<string, Track[]>();
  const byTitle = new Map<string, Track[]>();

  let trackCount = 0;

  for (const source of matchableSources) {
    const bucket = library[source];

    for (const id in bucket) {
      const track = bucket[id];

      // The buckets are Proxies handing back a NullTrack for anything unknown,
      // but `for in` only sees real keys, so this is belt and braces.
      if (!track || track.type === Source.Null) {
        continue;
      }

      trackCount += 1;

      const title = normalizeForMatch(track.title);
      const artist = normalizeForMatch(track.artist);
      const album = normalizeForMatch(track.album);

      if (title) {
        push(byTitle, title, track);

        if (artist) {
          push(byArtistTitle, artist + '|' + title, track);
          push(byArtistAlbumTitle, artist + '|' + album + '|' + title, track);
        }
      }
    }
  }

  return { byArtistAlbumTitle, byArtistTitle, byTitle, trackCount };
}

/**
 * The rules in order, strongest first. Each returns the candidates it found,
 * and the first rule that finds any decides the outcome - a later, weaker rule
 * never gets to overrule an earlier one, it only gets a turn when the earlier
 * one drew a blank.
 */
function candidatesFor(index: MatchIndex, itunesTrack: ItunesTrack) {
  const title = normalizeForMatch(itunesTrack.name);
  const artist = normalizeForMatch(itunesTrack.artist);
  const album = normalizeForMatch(itunesTrack.album);

  const rules: Array<{ method: MatchMethod; candidates?: Track[] }> = [];

  if (artist && title) {
    rules.push({
      method: MatchMethod.ArtistAlbumTitle,
      candidates: index.byArtistAlbumTitle.get(artist + '|' + album + '|' + title),
    });
    rules.push({
      method: MatchMethod.ArtistTitle,
      candidates: index.byArtistTitle.get(artist + '|' + title),
    });
  }

  /*
    The file name as a title.

    Fadila stores what the ID3 tag says, and for a file with no useful tags
    that is the file name the provider reported. iTunes kept the tag *and* the
    path, so when the tags disagree the path is the thing left to try. Worth
    410 matches out of 26,332 on the reference library, entirely in the folders
    of loose downloads.
  */
  const fileName = itunesTrack.location
    ? normalizeForMatch(fileNameFromLocation(itunesTrack.location))
    : '';

  if (fileName) {
    rules.push({ method: MatchMethod.FileName, candidates: index.byTitle.get(fileName) });
  }

  if (title) {
    rules.push({ method: MatchMethod.Title, candidates: index.byTitle.get(title) });
  }

  return rules;
}

/**
 * Candidates ordered by how far their length is from the iTunes track's, and
 * how far that is. Nearest first, so a caller that gives up still has the
 * best-looking one at the front.
 */
function rankByDuration(candidates: Track[], duration: number) {
  return candidates
    .map((track) => ({ track, distance: Math.abs(track.length - duration) }))
    .sort((a, b) => a.distance - b.distance);
}

/**
 * What the length says about several same-named candidates.
 *
 * `sure` is a match. `uncertain` is a near miss worth showing but not worth
 * importing on its own. `undefined` is a tie the length cannot help with.
 *
 * The two `sure` cases both require a candidate inside `durationTolerance`,
 * which is the window in which two numbers describe the same recording:
 *
 * 1. It is the only one inside the window.
 * 2. Several are inside it, but the nearest beats the runner-up by
 *    `clearMargin` - so the length did discriminate, and giving up would be
 *    throwing away information already in hand.
 *
 * Nothing inside the window is never a match, however lonely the nearest
 * candidate is. It can still be `uncertain`; see `looseTolerance`.
 */
type DurationVerdict =
  | { kind: 'sure'; track: Track }
  | { kind: 'uncertain'; track: Track; distance: number; runnerUpDistance: number }
  | undefined;

function judgeByDuration(candidates: Track[], duration: number | undefined): DurationVerdict {
  if (!duration) {
    return undefined;
  }

  const ranked = rankByDuration(candidates, duration);
  const nearest = ranked[0];
  const runnerUp = ranked[1];

  if (!nearest || !runnerUp) {
    return undefined;
  }

  if (nearest.distance < durationTolerance) {
    // (1) the only one that could be the same recording.
    if (runnerUp.distance >= durationTolerance) {
      return { kind: 'sure', track: nearest.track };
    }

    // (2) several could be, but one of them is markedly closer.
    if (runnerUp.distance - nearest.distance >= clearMargin) {
      return { kind: 'sure', track: nearest.track };
    }

    // (2b) one of them is an exact hit and the other is not close to one.
    if (
      nearest.distance <= exactHit &&
      runnerUp.distance >= exactHitMargin &&
      runnerUp.distance >= nearest.distance * exactHitRatio
    ) {
      return { kind: 'sure', track: nearest.track };
    }

    return undefined;
  }

  // Nothing is a clean match. If one candidate is nevertheless close and the
  // rest are nowhere near, say so and let the user decide.
  if (nearest.distance <= looseTolerance && runnerUp.distance >= nearest.distance * looseRatio) {
    return {
      kind: 'uncertain',
      track: nearest.track,
      distance: nearest.distance,
      runnerUpDistance: runnerUp.distance,
    };
  }

  return undefined;
}

/**
 * Whether a set of same-named candidates is one recording several times over:
 * all within `copyTolerance` of each other, end to end, and the nearest inside
 * the tolerance of the iTunes length so that it is this recording at all.
 */
function isSameRecording(candidates: Track[], duration: number) {
  let shortest = Infinity;
  let longest = -Infinity;
  let nearest = Infinity;

  for (const track of candidates) {
    shortest = Math.min(shortest, track.length);
    longest = Math.max(longest, track.length);
    nearest = Math.min(nearest, Math.abs(track.length - duration));
  }

  return longest - shortest <= copyTolerance && nearest < durationTolerance;
}

/**
 * The candidates an ambiguity is reported with: nearest first, and narrowed to
 * the ones that could be the same recording when more than one could be.
 *
 * Order matters because the head of this list is what "add the closest match"
 * in the dialog adds. Narrowing matters because a candidate a minute out is
 * not part of the decision the user is being shown.
 */
function reportedCandidates(candidates: Track[], duration: number) {
  const ranked = rankByDuration(candidates, duration);
  const within = ranked.filter((entry) => entry.distance < durationTolerance);

  return (within.length > 1 ? within : ranked).map((entry) => entry.track);
}

/**
 * Where in the Fadila library this iTunes track is, if it is anywhere.
 *
 * A rule that finds exactly one candidate matches outright. A rule that finds
 * several hands them to `judgeByDuration`, which either matches one, offers
 * one as `uncertain`, or leaves the whole set `ambiguous`. Nothing here ever
 * flips a coin: two "Untitled" cuts off one record a second apart come back
 * as an ambiguity, because that is the honest answer.
 */
export function matchItunesTrack(index: MatchIndex, itunesTrack: ItunesTrack): MatchResult {
  for (const rule of candidatesFor(index, itunesTrack)) {
    const candidates = rule.candidates;

    if (!candidates || candidates.length === 0) {
      continue;
    }

    if (candidates.length === 1) {
      return {
        kind: 'matched',
        track: candidates[0],
        method: rule.method,
        byDuration: false,
      };
    }

    const duration = itunesTrack.duration;
    const verdict = judgeByDuration(candidates, duration);

    if (verdict && verdict.kind === 'sure') {
      return {
        kind: 'matched',
        track: verdict.track,
        method: rule.method,
        byDuration: true,
      };
    }

    if (verdict && duration) {
      return {
        kind: 'uncertain',
        track: verdict.track,
        candidates: reportedCandidates(candidates, duration),
        method: rule.method,
        distance: verdict.distance,
        runnerUpDistance: verdict.runnerUpDistance,
      };
    }

    return {
      kind: 'ambiguous',
      candidates: duration ? reportedCandidates(candidates, duration) : candidates,
      method: rule.method,
      sameRecording: duration ? isSameRecording(candidates, duration) : false,
    };
  }

  return { kind: 'unmatched' };
}
